import { required, maxLength, numeric, decimal, sameAs } from 'vuelidate/lib/validators'
import {
  find,
  propEq,
  ifElse,
  compose,
  not,
  equals,
  hasPath,
  path,
  always,
  length,
  assoc,
  pipe,
  defaultTo,
  values,
  flatten,
  isEmpty,
  keys,
  clone,
  mapObjIndexed,
  set,
  lensProp,
  both,
  objOf,
  map,
  propSatisfies,
} from 'ramda'
import {
  isDurationGreaterThanGivenDuration,
  fitsInDuration,
  isResetTimeAllowed,
} from './customValidations.js'
import ResourceLinkingToExternalResource from './models/ResourceLinkingToExternalResource.js'
import DurationType from '@v/inventory/products/edit/models/DurationType.js'
import { productHasMultipleQuantityInDeal } from '@v/inventory/products/edit/productHasMultipleQuantityInDeal.js'
import { isFalsy } from '@v/util/briqRamdaExt.js'

// const shouldValidate = datatype => both(propEq('Optional', false), propEq('Datatype', datatype))
// const maybeValidate = ifElse(shouldValidate, always(validation), always(false))

const maybeValidate = (customField, datatype, validation) =>
  customField.Optional === false && customField.Datatype === datatype ? validation : false

const customFieldsValidation = mapObjIndexed((customField, key) => ({
  Values: {
    0: {
      required: customField.Optional === false ? required : false,
      numeric: maybeValidate(customField, 'Integer', numeric),
      decimal: maybeValidate(customField, 'Decimal', decimal),
      sameAs: maybeValidate(
        customField,
        'Boolean',
        sameAs(() => true),
      ),
    },
  },
}))

export const customFieldDefinitionsToValidation = pipe(
  customFieldsValidation,
  objOf('CustomFields'),
)

export const integrationFieldDefinitionsToValidation = pipe(
  mapObjIndexed(integration => ({
    Fields: customFieldsValidation(integration.FieldDefinitions),
  })),
  objOf('IntegrationFields'),
)

export const productDurationFieldsValidation = (schedulingBehavior, selectedResource, product) => {
  const notZeroInIso = value => value !== 'PT0M' && value !== 'PT0S'
  const notZero = value => value !== 0
  let validationRules = {}
  if (schedulingBehavior === 'fixedDuration')
    validationRules = {
      ...validationRules,
      CustomDuration: {
        notZeroInIso,
        required: !selectedResource?.ProductsAreDefaultScheduled ? required : false,
      },
    }
  if (schedulingBehavior === 'perParticipants')
    validationRules = {
      ...validationRules,
      CustomDuration: { notZeroInIso },
      NumberOfParticipantsPerDuration: { required, notZero },
    }

  if (schedulingBehavior === 'perParticipants' || schedulingBehavior === 'parallel')
    validationRules = {
      ...validationRules,
      StartDuration: {
        fitsInMaximumDuration: fitsInDuration(product.ProductDurationConfig.MaximumDuration),
      },
      MaximumDuration: {
        isMinimumDurationLowerThanMaximumDuration: isDurationGreaterThanGivenDuration(
          product.ProductDurationConfig.MinimumDuration,
        ),
      },
    }

  validationRules = {
    ...validationRules,
    ResetTime: {
      isResetTimeAllowed: isResetTimeAllowed(product.ProductDurationConfig.DurationType),
    },
  }
  return validationRules
}

function canAddExternalProducts(externalType, featureFlags) {
  const pathTo = [externalType, 'LinkingToExternalProduct']
  const allowedByFeatureFlags = ifElse(
    hasPath(pathTo),
    compose(not, equals(ResourceLinkingToExternalResource.NotAllowed), path(pathTo)),
    always(false),
  )
  return allowedByFeatureFlags(featureFlags)
}

export const externalProductIdsValidation = (externalType, featureFlags) => {
  return canAddExternalProducts(externalType, featureFlags)
    ? { ExternalProductIds: { required } }
    : {}
}

export const segmentPriceValidation = product => value =>
  product.PriceType === 'PerPerson' || isFalsy(value)

export const minMaxPriceValidation = product => _value => {
  return !isFalsy(product.MinimumPrice) && !isFalsy(product.MaximumPrice)
    ? product.MinimumPrice <= product.MaximumPrice
    : true
}

const generateProductQuantityValidationMessage = linkedDeals => {
  if (linkedDeals.length === 0) return
  const appendPlural = linkedDeals.length > 1 ? 's' : ''
  const listOfLinkedDeals = linkedDeals.join(', ')
  return `The quantity of this product linked in deal${appendPlural}: "${listOfLinkedDeals}" is more than 1`
}

export const getAuxiliaryValidationError = (product, forEditorModel, initialDurationConfig) => {
  // ===============================================================================================
  // # This check is here because we can receive an empty object from API, but we can't save like that
  // check for array is based on the new ActiveSegments structure so check on empty object should be removed soon
  if (equals(product.ActiveSegments, {}) || equals(product.ActiveSegments, [])) {
    return {
      userMessage: 'Active segments in save model is invalid',
      errorMessage: 'ActiveSegments may not be an empty object/array',
    }
  }
  // ===============================================================================================
  // # If you have active classifications, you must have active segments
  const resourceHasSegments = pipe(
    find(i => i.ResourceKey === product.ResourceKey),
    defaultTo({}),
    propSatisfies(x => length(x) > 0, 'Segments'),
  )(forEditorModel.ResourceSegments)
  const hasActiveClassifications = length(keys(product.ActiveSegments)) > 0
  const noActiveSegments = pipe(values, flatten, isEmpty)(product.ActiveSegments)
  if (resourceHasSegments && hasActiveClassifications && noActiveSegments) {
    return {
      userMessage: 'At least one segment must be active',
    }
  }
  // ===============================================================================================
  // # if you switch from non-scheduled to scheduled, ensure that the product is not in a deal with quantity > 1, otherwise don't save and show warning
  const wasNonScheduled = initialDurationConfig.DurationType === DurationType.NoDuration
  const isNowScheduled = product.ProductDurationConfig.DurationType !== DurationType.NoDuration
  if (wasNonScheduled && isNowScheduled) {
    if (productHasMultipleQuantityInDeal(product.Key)(forEditorModel)) {
      return {
        userMessage:
          'Cannot turn this into a scheduled product because there are deals where this product has quantity > 1.',
      }
    }
  }
  // ===============================================================================================
}

export const resourceIsActive = selectedResource => _value =>
  selectedResource ? selectedResource.IsActive : false
